import { useCallback, useRef, useState } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export interface InlineUploadDropzoneProps {
  accept: string;
  multiple?: boolean;
  allowCamera?: boolean;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
  title?: string;
  hint?: string;
  className?: string;
  onFilesSelected: (files: FileList) => void | Promise<void>;
}

export default function InlineUploadDropzone({
  accept,
  multiple = false,
  allowCamera = true,
  disabled = false,
  loading = false,
  error = null,
  title = 'Drag files here or click to choose',
  hint,
  className = '',
  onFilesSelected,
}: InlineUploadDropzoneProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0 || disabled || loading) return;
      void onFilesSelected(files);
    },
    [disabled, loading, onFilesSelected]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
      handleFiles(event.dataTransfer.files);
    },
    [handleFiles]
  );

  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={disabled || loading ? -1 : 0}
        onClick={() => {
          if (!disabled && !loading) fileInputRef.current?.click();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (!disabled && !loading) fileInputRef.current?.click();
          }
        }}
        onDrop={handleDrop}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!disabled && !loading) setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragging(false);
        }}
        className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors ${
          disabled || loading
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
            : isDragging
              ? 'border-[#E8621A] bg-[#FEF0E7]/40 cursor-pointer'
              : 'border-gray-200 bg-[#FAFAFA] hover:border-[#E8621A] cursor-pointer'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          disabled={disabled || loading}
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept={accept.includes('image') ? 'image/*' : accept}
          capture="environment"
          className="hidden"
          disabled={disabled || loading}
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = '';
          }}
        />

        <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-[#9CA3AF]">
          {loading ? (
            <i className="ri-loader-4-line animate-spin text-2xl" />
          ) : (
            <i className="ri-upload-cloud-2-line text-2xl" />
          )}
        </div>
        <p className="text-sm font-medium text-[#374151]">{loading ? 'Uploading…' : title}</p>
        {hint && <p className="text-[11px] text-[#9CA3AF] mt-1">{hint}</p>}

        {isMobile && allowCamera && accept.includes('image') && !loading && !disabled && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                cameraInputRef.current?.click();
              }}
              className="py-2 rounded-lg border border-gray-200 bg-white text-[11px] font-semibold text-[#374151] cursor-pointer"
            >
              <i className="ri-camera-line mr-1" />
              Camera
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                fileInputRef.current?.click();
              }}
              className="py-2 rounded-lg border border-gray-200 bg-white text-[11px] font-semibold text-[#374151] cursor-pointer"
            >
              <i className="ri-image-line mr-1" />
              Gallery
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 bg-[#FEF2F2] rounded-xl px-3 py-2 text-[#EF4444] text-xs">{error}</div>
      )}
    </div>
  );
}
